import React from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip/tooltip';
import { LinkButton } from '@epoch-ui/components/LinkButton/LinkButton';
import { Icon } from '@epoch-ui/components/Icon';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { EPOCH_COMMUNICATION_NOTIFICATION } from '../../types/communication.types';

import { NotificationCard as NotificationCardComponent } from './NotificationCard';

/**
 * Render list of notifications that will be sent out by Epoch.
 * 1. Event questionnaire reminder
 * 2. Feedback
 * 3. Feedback 'send now'
 * 4. Check-in
 * @returns
 */
export const EpochCommunication: React.FC<{
  notifications: EPOCH_COMMUNICATION_NOTIFICATION[];
}> = ({ notifications }) => {
  const event = useEditableEvent();

  const isCancelled = !!event.scheduledDeletionAt;

  /**
   * TODO:
   * Add condition to not show this component if there are no notifications to show.
   * eg; No self-checkins, No event questionnaire, No feedback, etc.
   */
  return (
    <div>
      <div className={cn('tw-flex tw-items-center tw-gap-1', isCancelled && 'tw-opacity-40')}>
        <h2 className="tw-m-0 tw-text-xl tw-font-medium tw-leading-7">Epoch communications</h2>
        <Tooltip>
          <TooltipTrigger className="tw-p-0" disabled={isCancelled}>
            <Icon name="info" size={20} className="tw-text-medium-grey" />
          </TooltipTrigger>
          <TooltipContent side="top" align="start" className="tw-w-[336px]">
            Update the event settings to make changes when and how communications are sent.
            <br />
            <LinkButton
              href="https://help.epochapp.com/articles/automated-epoch-communications"
              target="_blank"
              rel="noreferrer"
              variant="link"
              size="sm"
              className="tw-h-4 tw-p-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-white hover:tw-underline"
            >
              Learn more
            </LinkButton>
          </TooltipContent>
        </Tooltip>
      </div>
      <p
        className={cn(
          'tw-m-0 tw-mt-1 tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey',
          isCancelled && 'tw-opacity-40'
        )}
      >
        Automated messages guests receive based on event settings
      </p>
      <div className="tw-mt-4">
        {notifications.map((notification, index) => (
          <div
            key={`${notification.title}-${notification.id}`}
            className={cn(
              'tw-border tw-border-solid tw-border-gray-300 tw-p-4',
              index === 0 && 'tw-rounded-t-lg',
              index === notifications.length - 1 && 'tw-rounded-b-lg',
              index !== notifications.length - 1 && 'tw-border-b-0'
            )}
          >
            <NotificationCardComponent epochCommunication={notification} disabled={isCancelled} />
          </div>
        ))}
      </div>
    </div>
  );
};
